/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import { type JSX, useCallback, useMemo } from 'react';

import noop from 'lodash/noop';
import { type MessageDescriptor, useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import AkFlag, { type FlagProps } from '@atlaskit/flag';
import InfoIcon from '@atlaskit/icon/core/status-information';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { B300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { type Collaborators, type CrossJoinIntegration } from '../../common/types';
import { FlagCompatibleAvatarGroup } from '../../common/ui/flag-compatible-avatar-group';
import { getProductDisplayName, type Product } from '../../common/ui/products';
import { withLocalIntl } from '../../common/utils/i18n/intl-provider';

import { LinkButton } from './link-button';
import { messages } from './messages';

type RequestAccessFlagMessages = {
	ctaButton: MessageDescriptor;
	descriptionWithCollaborators: MessageDescriptor;
	descriptionWithoutCollaborators: MessageDescriptor;
	optOutButton: MessageDescriptor;
	remindMeLaterButton?: MessageDescriptor;
	title: MessageDescriptor;
	titleNotInterested: MessageDescriptor;
	titleReminder: MessageDescriptor;
};

const messageLibrary = ((): Record<CrossJoinIntegration, RequestAccessFlagMessages> => ({
	crossJoinNudgesV3: {
		ctaButton: messages.joinButton,
		remindMeLaterButton: messages.remindMeLaterButton,
		optOutButton: messages.notInterestedButton,
		descriptionWithCollaborators: FeatureGates.getExperimentValue(
			'project-terminology-refresh',
			'isEnabled',
			false,
		)
			? messages.descriptionJiraGalaxia
			: messages.descriptionJira,
		descriptionWithoutCollaborators: FeatureGates.getExperimentValue(
			'project-terminology-refresh',
			'isEnabled',
			false,
		)
			? messages.descriptionJiraGalaxia
			: messages.descriptionJira,
		title: messages.title,
		titleReminder: messages.titleReminder,
		titleNotInterested: messages.titleNotInterested,
	},
	crossJoinNudgesConfluenceToJpd: {
		ctaButton: messages.getStartedButton,
		remindMeLaterButton: messages.remindMeLaterButton,
		optOutButton: messages.doNotShowAgainButton,
		descriptionWithCollaborators: messages.descriptionJpdNudge,
		descriptionWithoutCollaborators: messages.descriptionJpdNudge,
		title: messages.titleJpdNudge,
		titleReminder: messages.titleJpdNudge,
		titleNotInterested: messages.titleJpdNudge,
	},
	crossJoinNudgesJiraToConfluence: {
		ctaButton: messages.getStartedButton,
		optOutButton: messages.remindMeLaterButton,
		descriptionWithCollaborators: messages.descriptionJiraConfluenceMsrNudgeWithCollaborators,
		descriptionWithoutCollaborators: messages.descriptionJiraConfluenceMsrNudgeNoCollaborators,
		title: messages.titleJiraConfluenceMsrNudge,
		titleReminder: messages.titleJiraConfluenceMsrNudge,
		titleNotInterested: messages.titleJiraConfluenceMsrNudge,
	},
	crossJoinNudgesJiraToJpd: {
		ctaButton: messages.getStartedButton,
		optOutButton: messages.remindMeLaterButton,
		descriptionWithCollaborators: messages.descriptionJpdNudge,
		descriptionWithoutCollaborators: messages.descriptionJpdNudge,
		title: messages.titleJpdNudge,
		titleReminder: messages.titleJpdNudge,
		titleNotInterested: messages.titleJpdNudge,
	},
	crossJoinNudgesConfluenceToJiraMsr: {
		ctaButton: messages.getStartedButton,
		optOutButton: messages.remindMeLaterButton,
		descriptionWithCollaborators: messages.descriptionJira,
		descriptionWithoutCollaborators: messages.descriptionJira,
		title: messages.titleJiraNudgeMsr,
		titleReminder: messages.titleJiraNudgeMsr,
		titleNotInterested: messages.titleJiraNudgeMsr,
	},
	crossJoinNudgesConfluenceToJpdMsr: {
		ctaButton: messages.getStartedButton,
		optOutButton: messages.doNotShowAgainButton,
		descriptionWithCollaborators: messages.descriptionJpdNudge,
		descriptionWithoutCollaborators: messages.descriptionJpdNudge,
		title: messages.titleJpdNudge,
		titleReminder: messages.titleJpdNudge,
		titleNotInterested: messages.titleJpdNudge,
	},
	crossJoinNudgesConfluenceToLoom: {
		ctaButton: messages.getStartedButton,
		remindMeLaterButton: messages.remindMeLaterButton,
		optOutButton: messages.notInterestedButton,
		descriptionWithCollaborators: messages.descriptionLoomNudge,
		descriptionWithoutCollaborators: messages.descriptionLoomNudge,
		title: messages.titleLoomNudge,
		titleReminder: messages.titleLoomNudge,
		titleNotInterested: messages.titleLoomNudge,
	},
}))();

const MAX_NUMBER_OF_COLLABORATORS = 10;

const styles = cssMap({
	descriptionWrapper: {
		overflow: 'visible',
		marginTop: token('space.0', '0px'),
		marginInline: token('space.0', '0px'),
		marginBottom: token('space.100', '8px'),
	},
});

export type Props = {
	collaborators: Collaborators;
	descriptionWithCollaborators?: string | null;
	flagComponent?: React.ElementType<FlagProps>;
	id: FlagProps['id'];
	integration: CrossJoinIntegration;
	onDismiss: (id: FlagProps['id']) => void;
	onOptoutAndDismiss: () => unknown;
	onSnoozeAndDismiss: () => unknown;
	onSubmit: () => Promise<void>;
	targetProduct: Product;
	testId?: string;
	viewCount: number;
};

export const RequestAccessPromptFlagNoIntl = ({
	integration,
	collaborators,
	descriptionWithCollaborators = null, // Can be supplied to override the description when there are multiple collaborators
	targetProduct,
	viewCount,
	onSubmit,
	onDismiss,
	onSnoozeAndDismiss,
	onOptoutAndDismiss,
	flagComponent: FlagComponent = AkFlag,
	...props
}: Props): JSX.Element => {
	const handleOnSubmit = useCallback(async () => {
		await onSubmit();
	}, [onSubmit]);
	const { formatMessage } = useIntl();
	const messages = messageLibrary[integration];
	const customFlagFooterProps = useMemo(
		(): Pick<FlagProps, 'actions' | 'linkComponent'> => ({
			// Workaround to achieve primary button + avatars in the footer.
			//
			// How this works:
			// We pass a dummy `action` that gets displayed in the Flag's footer
			// by the custom `linkComponent`. The component displays our actual footer.
			// This way, we don't occupy the limited space inside `description`.
			//
			// Why not use `actions` normally?
			// Because our Avatar Group is not an action, and even our Submit button
			// should be bold & blue (appearance="primary") which is not supported
			// by the AK Flag.
			//
			// Why not put this in `description`?
			// Because that one has a max height equivalent to 5 lines of text,
			// and our footer is taller than 2, causing an unwanted scrollbar
			// to show.
			actions: [
				{
					content: '',
					onClick: noop,
				},
			],
			linkComponent: () => {
				let secondCTA: React.ReactNode = null;

				if (viewCount === 1 && messages['remindMeLaterButton']) {
					secondCTA = (
						<LinkButton onClick={onSnoozeAndDismiss}>
							{formatMessage(messages['remindMeLaterButton'])}
						</LinkButton>
					);
				} else if (viewCount > 1) {
					secondCTA = (
						<LinkButton onClick={onOptoutAndDismiss}>
							{formatMessage(messages['optOutButton'])}
						</LinkButton>
					);
				}

				return (
					<Inline
						alignInline="start"
						grow="fill"
						alignBlock="center"
						separator="·"
						space="space.150"
					>
						<LinkButton onClick={handleOnSubmit}>{formatMessage(messages['ctaButton'])}</LinkButton>
						{secondCTA}
					</Inline>
				);
			},
		}),
		[viewCount, handleOnSubmit, formatMessage, messages, onSnoozeAndDismiss, onOptoutAndDismiss],
	);

	const customIcon = useMemo(() => {
		return collaborators.length > 0 ? (
			<FlagCompatibleAvatarGroup
				position="top"
				data={collaborators.slice(0, MAX_NUMBER_OF_COLLABORATORS).map((user) => ({
					name: user.name,
					src: user.avatar,
					appearance: 'circle',
				}))}
				boundariesElement="scrollParent"
				size="small"
			/>
		) : (
			<InfoIcon color={token('color.icon.information', B300)} label="" />
		);
	}, [collaborators]);

	const title =
		viewCount === 0
			? messages['title']
			: viewCount === 1
				? messages['titleReminder']
				: messages['titleNotInterested'];
	const infoFlagTitle = formatMessage(title, { product: getProductDisplayName(targetProduct) });

	const description =
		collaborators.length === 0
			? formatMessage(messages['descriptionWithoutCollaborators'])
			: descriptionWithCollaborators || formatMessage(messages['descriptionWithCollaborators']);

	return (
		<FlagComponent
			{...props}
			{...customFlagFooterProps}
			onDismissed={viewCount === 0 ? onDismiss : onSnoozeAndDismiss}
			title={infoFlagTitle}
			description={
				<Box xcss={styles.descriptionWrapper}>
					<Text as="p" maxLines={3}>
						{description}
					</Text>
				</Box>
			}
			icon={customIcon}
		/>
	);
};

export const RequestAccessPromptFlag: (props: Props) => JSX.Element = withLocalIntl(
	RequestAccessPromptFlagNoIntl,
);
